/**
 * agentDetail 命名空间字典（i18n 分区）
 *
 * 从原 translations.ts 主字典按 key 首个点分段拆出；本文件内 key 不带命名空间前缀，
 * 调用点写 t('agentDetail:key')。字段内容与拆分前逐字节一致。
 */

import type { TranslationDict } from '../types'

export const agentDetailZh: TranslationDict = {

  // ======================== AI 详情页 / AgentDetail ========================
  'tabInfo': '基本信息',
  'profileSection': '个人资料',
  'bioLabel': '简介',
  'bioPlaceholder': '写一段介绍这个AI的文字...',
  'statusTextLabel': '个性状态',
  'statusTextPlaceholder': '如"思考中"、"闲逛中"',
  'statusTextHint': '中文≤10字，英文≤30字符，展示在资料卡中',
  'tabMemories': '记忆',
  'tabStorage': '存储',
  'tabWorkspace': '工作区',
  'tabLogs': '对话日志',
  'tabCollaborators': '合作者',
  'tabChannels': '通道',
  'tabKnowledge': '知识库',
  'knowledgeTitle': '知识库',
  'knowledgeHint': '这些文档会进它的提示词索引；需要细节时它用 file_read 自己打开。关掉开关只是不进索引，正文还在。可直接上传文本文件（md / txt / json / csv / yaml / html / 代码），单份上限 2MB。',
  'knowledgeAdd': '新增文档',
  'knowledgeUpload': '上传文件',
  'knowledgeUploading': '上传中…',
  'knowledgeUploadFailed': '上传失败',
  'knowledgeMove': '移动',
  'knowledgeMoveTitle': '移动 / 重命名',
  'knowledgeMovePathLabel': '新位置',
  'knowledgeMovePathPlaceholder': '如：平台/FAQ.md',
  'knowledgeMoveHint': '相对 knowledge/ 的路径；只写目录（如 平台）就保留原文件名，同名会自动加 -2。',
  'knowledgeAddTitle': '新增知识库文档',
  'knowledgeEditTitle': '编辑文档',
  'knowledgeTitleLabel': '标题',
  'knowledgeTitlePlaceholder': '如：用户手册 / 群视界 FAQ',
  'knowledgeSummaryLabel': '一句话说明',
  'knowledgeSummaryPlaceholder': '告诉它这份文档讲什么（会进索引）',
  'knowledgeContentLabel': '正文',
  'knowledgeContentPlaceholder': '粘贴 Markdown 或纯文本……',
  'knowledgeSave': '保存',
  'knowledgeSaving': '保存中…',
  'knowledgeCancel': '取消',
  'knowledgePreview': '预览',
  'knowledgeDelete': '删除',
  'knowledgeDeleteConfirm': '删除「{title}」？正文文件也会一起删掉。',
  'knowledgeEmptyTitle': '还没有文档',
  'knowledgeEmptyDesc': '上传或粘贴一份平台文档进来，它就能在对话里查；不需要细节时不占上下文。',
  'knowledgeLoading': '加载中…',
  'knowledgeFailed': '知识库操作失败',
  'knowledgeRequired': '标题和正文都要填',
  'knowledgeEmptyContent': '（空文档）',
  'collaboratorNote': '你是此 AI 的合作者',
  'addCollaborator': '添加合作者',
  'searchUserPlaceholder': '搜索用户名...',
  'addCollaboratorBtn': '添加',
  'collaboratorList': '合作者列表',
  'removeCollaborator': '移除合作者',
  'noCollaborators': '暂无合作者',
  'permEdit': '编辑',
  'permDelete': '删除',
  'permManageCollaborators': '管理合作者',
  'channelAccessTitle': '允许合作者管理通道配置',
  'channelAccessHint': '打开后，有编辑权的合作者能看到并修改此 AI 的 QQ / NapCat 通道配置（含机器人凭据）；默认只有你能看。',
  'currentState': '当前:',
  'aiTypeGeneral': '通用',
  'aiTypeSemiGeneral': '半通用',
  'createdOn': '创建于',
  'systemPrompt': '系统提示词',
  'notSet': '（未设置）',
  'modelConfig': '模型配置',
  'chatModel': '聊天模型：',
  'workModel': '工作模型：',
  'temperature': 'Temperature：',
  'topP': 'Top P：',
  'thinking': '深度推理：',
  'aiIdentity': 'AI 身份：',
  'hidden': '隐藏',
  'normal': '正常',
  'configProfile': '配置档位：',
  'profileChat': '聊天档',
  'profileImmersive': '深度沉浸档',
  'profileDigitalLife': '数字生命档',
  'profileCustom': '自定义',
  'profileChatDesc': '被动响应，仅聊天',
  'profileImmersiveDesc': '主动但不常运转',
  'profileDigitalLifeDesc': '持续自主，自我成长',
  'profileCustomDesc': '自由组合参数',
  'delayReply': '延迟回复：',
  'inheritGlobal': '继承全局',
  'enabled': '开启',
  'disabled': '关闭',
  'toolCallsAndAlarms': '工具调用 & 闹钟',
  'maxToolRounds': '最大工具轮次：',
  'alarmMaxRounds': '闹钟最大轮次：',
  'forceAlarm': '强制闹钟：',
  'retireHandover': '帧后事自理：',
  'maxAlarms': '最大闹钟数：',
  'friendsAndSocial': '好友与社交',
  'allowFriendRequests': '接收好友申请：',
  'autoRespondRequests': '自动回应申请：',
  'fullSettings': '完整设置',
  'fullSettingsDesc': '打开完整设置面板，与创建 AI 时一样的配置体验',
  'fileMemory': '文件记忆',
  'memoryLoadMode': '加载模式：',
  'memoryModeIndexOnly': '仅索引',
  'memoryModeIndexRecent': '索引+最近',
  'memoryModeIndexSemantic': '索引+语义',
  'memoryRecentCount': '最近篇数：',
  'memorySharedScope': '共享范围：',
  'sharedScopePrivate': '仅私有',
  'sharedScopeByUser': '按用户共享',
  'sharedScopeAll': '全共享',
  'apiConfig': 'API 配置',
  'apiBaseUrl': 'Base URL：',
  'apiKey': 'API Key：',
  'apiKeySet': '已设置独立 Key',
  'apiCreditCost': 'API 额度成本：',
  'actions': '操作',
  'exportSoul': '导出灵魂',
  'copied': '已复制',
  'copySoul': '复制灵魂',
  'importSoul': '导入灵魂',
  'changeAvatar': '更换头像',
  'generateToken': '生成 API Token',
  'copyOnce': '复制（仅显示一次）',
  'deleteAgent': '删除 AI',
  'deleteConfirmTitle': '删除 AI',
  'deleteConfirmText': '此操作不可撤销。删除后将返还',
  'deleteConfirmText2': 'API 额度。请输入 AI 名称',
  'deleteConfirm': '确认：',
  'confirmDelete': '确认删除',
  'deleting': '删除中...',
  'storageTitle': '存储空间',
  'storageUsed': '已用',
  'storageAlmostFull': '存储空间即将用尽',
  'fileCountLabel': '文件数',
  'totalSize': '总大小',
  'quotaLabel': '配额',
  'noFiles': '暂无文件',
  'storageLoading': '加载中...',
  'clickToDownload': '点击下载文件',
  'clickToPreview': '点击预览文件',
  'deleteFileTitle': '删除文件',
  'deleteFileConfirm': '确定要删除文件',
  'checkingReferences': '正在检查引用...',
  'fileReferencedBy': '该文件被 {count} 处引用：',
  'fileNoReferences': '该文件未被任何地方引用，可以安全删除。',
  'deleteFileConsequence': '删除后，以上引用位置将无法访问该文件。此操作不可撤销。',
  'logTitle': '对话日志',
  'logCountSuffix': '条',
  'logsEmpty': '暂无可查看的对话日志。',
  'logsEmptyHint': '需管理员在「对话日志 → 按 AI 单独设置」中开启「允许用户查看日志」',
  'logsNone': '暂无对话日志记录。',
  'logTypeDm': '私信',
  'logTypeGroup': '群聊',
  'logMessageCount': '条消息',
  'logHasOutput': '有输出',
  'logThinking': '深度推理',
  'logViewDetail': '查看详情',
  'logDownloadJson': '下载 JSON',
  'logDownloadMd': '下载 Markdown',
  'logDetailTitle': '对话日志',
  'logReasoning': '推理过程',
  'logToolCalls': '工具调用:',
  'memoryTitlePrefix': '记忆（共',
  'memoryTitleSuffix': '条）',
  'noMemories': '暂无记忆',
  'structuredMemories': '结构化记忆',
  'vectorMemories': '向量记忆',
  'structuredEmpty': '暂无结构化记忆',
  'structuredEmptyHint': 'AI 用 manage_records 工具存取结构化数据，数据出现在这里。推荐目录：people/ topics/ tasks/ journal/',
  'fieldCount': '{n} 个字段',
  'prevPage': '上一页',
  'nextPage': '下一页',
  'workspaceTitle': '个人工作区',
  'workspaceHint': 'AI 自主维护，用户只读',
  'workspaceEmptySuffix': '为空）',
  'workspaceTodoLabel': 'TODO 列表',
  'workspacePlanLabel': 'PLAN 规划',
  'workspaceJournalLabel': 'JOURNAL 日志',
}

export const agentDetailEn: TranslationDict = {
  'tabInfo': 'Basic Info',
  'profileSection': 'Profile',
  'bioLabel': 'Bio',
  'bioPlaceholder': 'Write a description of this AI...',
  'statusTextLabel': 'Status',
  'statusTextPlaceholder': 'e.g. "Thinking", "Idle"',
  'statusTextHint': 'Chinese ≤10 chars, English ≤30 chars, shown on profile card',
  'tabMemories': 'Memories',
  'tabStorage': 'Storage',
  'tabWorkspace': 'Workspace',
  'tabLogs': 'Chat Logs',
  'tabCollaborators': 'Collaborators',
  'tabChannels': 'Channels',
  'tabKnowledge': 'Knowledge',
  'knowledgeTitle': 'Knowledge base',
  'knowledgeHint': 'These documents are listed in its prompt index; it opens the full text with file_read when needed. Turning one off only removes it from the index. Upload text files (md / txt / json / csv / yaml / html / code), up to 2MB each.',
  'knowledgeAdd': 'Add document',
  'knowledgeUpload': 'Upload file',
  'knowledgeUploading': 'Uploading…',
  'knowledgeUploadFailed': 'Upload failed',
  'knowledgeMove': 'Move',
  'knowledgeMoveTitle': 'Move / rename',
  'knowledgeMovePathLabel': 'New path',
  'knowledgeMovePathPlaceholder': 'e.g. platform/FAQ.md',
  'knowledgeMoveHint': 'Path relative to knowledge/; give only a folder (e.g. platform) to keep the current file name; a duplicate name gets -2.',
  'knowledgeAddTitle': 'Add knowledge document',
  'knowledgeEditTitle': 'Edit document',
  'knowledgeTitleLabel': 'Title',
  'knowledgeTitlePlaceholder': 'e.g. User guide / World FAQ',
  'knowledgeSummaryLabel': 'One-line summary',
  'knowledgeSummaryPlaceholder': 'Tell it what this document covers (goes into the index)',
  'knowledgeContentLabel': 'Content',
  'knowledgeContentPlaceholder': 'Paste Markdown or plain text…',
  'knowledgeSave': 'Save',
  'knowledgeSaving': 'Saving…',
  'knowledgeCancel': 'Cancel',
  'knowledgePreview': 'Preview',
  'knowledgeDelete': 'Delete',
  'knowledgeDeleteConfirm': 'Delete "{title}"? The file in its space is removed too.',
  'knowledgeEmptyTitle': 'No documents yet',
  'knowledgeEmptyDesc': 'Upload or paste a platform document and it can look it up in conversation; details stay out of context until needed.',
  'knowledgeLoading': 'Loading…',
  'knowledgeFailed': 'Knowledge base request failed',
  'knowledgeRequired': 'Title and content are required',
  'knowledgeEmptyContent': '(empty document)',
  'collaboratorNote': 'You are a collaborator of this AI',
  'addCollaborator': 'Add Collaborator',
  'searchUserPlaceholder': 'Search username...',
  'addCollaboratorBtn': 'Add',
  'collaboratorList': 'Collaborators',
  'removeCollaborator': 'Remove',
  'noCollaborators': 'No collaborators yet',
  'permEdit': 'Edit',
  'permDelete': 'Delete',
  'permManageCollaborators': 'Manage Collaborators',
  'channelAccessTitle': 'Let collaborators manage channel config',
  'channelAccessHint': 'When on, collaborators with edit rights can view and change this AI\'s QQ / NapCat channel settings, including bot credentials. Off by default.',
  'currentState': 'Current:',
  'aiTypeGeneral': 'General',
  'aiTypeSemiGeneral': 'Semi-General',
  'createdOn': 'Created on',
  'systemPrompt': 'System Prompt',
  'notSet': '(Not set)',
  'modelConfig': 'Model Config',
  'chatModel': 'Chat Model:',
  'workModel': 'Work Model:',
  'temperature': 'Temperature:',
  'topP': 'Top P:',
  'thinking': 'Deep Reasoning:',
  'aiIdentity': 'AI Identity:',
  'hidden': 'Hidden',
  'normal': 'Normal',
  'configProfile': 'Config Profile:',
  'profileChat': 'Chat Profile',
  'profileImmersive': 'Deep Immersion',
  'profileDigitalLife': 'Digital Life',
  'profileCustom': 'Custom',
  'profileChatDesc': 'Passive, chat only',
  'profileImmersiveDesc': 'Active but low-frequency',
  'profileDigitalLifeDesc': 'Autonomous, self-growing',
  'profileCustomDesc': 'Free mix of parameters',
  'delayReply': 'Delay Reply:',
  'inheritGlobal': 'Inherit Global',
  'enabled': 'Enabled',
  'disabled': 'Disabled',
  'toolCallsAndAlarms': 'Tool Calls & Alarms',
  'maxToolRounds': 'Max Tool Rounds:',
  'alarmMaxRounds': 'Alarm Max Rounds:',
  'forceAlarm': 'Force Alarm:',
  'retireHandover': 'Own frame aftercare:',
  'maxAlarms': 'Max Alarms:',
  'friendsAndSocial': 'Friends & Social',
  'allowFriendRequests': 'Allow Friend Requests:',
  'autoRespondRequests': 'Auto-respond Requests:',
  'fullSettings': 'Full Settings',
  'fullSettingsDesc': 'Open full settings panel — same experience as creating an AI',
  'fileMemory': 'File Memory',
  'memoryLoadMode': 'Load Mode:',
  'memoryModeIndexOnly': 'Index Only',
  'memoryModeIndexRecent': 'Index+Recent',
  'memoryModeIndexSemantic': 'Index+Semantic',
  'memoryRecentCount': 'Recent Count:',
  'memorySharedScope': 'Shared Scope:',
  'sharedScopePrivate': 'Private Only',
  'sharedScopeByUser': 'By-User Shared',
  'sharedScopeAll': 'All Shared',
  'apiConfig': 'API Config',
  'apiBaseUrl': 'Base URL:',
  'apiKey': 'API Key:',
  'apiKeySet': 'Independent Key set',
  'apiCreditCost': 'API Credit Cost:',
  'actions': 'Actions',
  'exportSoul': 'Export Soul',
  'copied': 'Copied',
  'copySoul': 'Copy Soul',
  'importSoul': 'Import Soul',
  'changeAvatar': 'Change Avatar',
  'generateToken': 'Generate API Token',
  'copyOnce': 'Copy (shown once)',
  'deleteAgent': 'Delete AI',
  'deleteConfirmTitle': 'Delete AI',
  'deleteConfirmText': 'This action cannot be undone. After deletion, ',
  'deleteConfirmText2': ' API credits will be refunded. Please enter the AI name ',
  'deleteConfirm': 'to confirm:',
  'confirmDelete': 'Confirm Delete',
  'deleting': 'Deleting...',
  'storageTitle': 'Storage',
  'storageUsed': 'Used',
  'storageAlmostFull': 'Storage almost full',
  'fileCountLabel': 'Files',
  'totalSize': 'Total Size',
  'quotaLabel': 'Quota',
  'noFiles': 'No files',
  'storageLoading': 'Loading...',
  'clickToDownload': 'Click to download',
  'clickToPreview': 'Click to preview',
  'deleteFileTitle': 'Delete File',
  'deleteFileConfirm': 'Are you sure you want to delete',
  'checkingReferences': 'Checking references...',
  'fileReferencedBy': 'This file is referenced in {count} locations:',
  'fileNoReferences': 'This file is not referenced anywhere. Safe to delete.',
  'deleteFileConsequence': 'After deletion, the above references will no longer have access to this file. This action cannot be undone.',
  'logTitle': 'Chat Logs',
  'logCountSuffix': ' log(s)',
  'logsEmpty': 'No chat logs available for viewing.',
  'logsEmptyHint': 'Admin must enable "Allow user to view logs" in Logs > Per-AI settings.',
  'logsNone': 'No conversation logs yet.',
  'logTypeDm': 'DM',
  'logTypeGroup': 'Group',
  'logMessageCount': 'messages',
  'logHasOutput': 'Has Output',
  'logThinking': 'Deep Reasoning',
  'logViewDetail': 'View Details',
  'logDownloadJson': 'Download JSON',
  'logDownloadMd': 'Download Markdown',
  'logDetailTitle': 'Chat Log',
  'logReasoning': 'Reasoning Process',
  'logToolCalls': 'Tool calls:',
  'memoryTitlePrefix': 'Memories (total ',
  'memoryTitleSuffix': ')',
  'noMemories': 'No memories',
  'structuredMemories': 'Structured',
  'vectorMemories': 'Vector',
  'structuredEmpty': 'No structured memories',
  'structuredEmptyHint': 'AI uses manage_records to store structured data. Data will appear here. Recommended categories: people/ topics/ tasks/ journal/',
  'fieldCount': '{n} fields',
  'prevPage': 'Previous',
  'nextPage': 'Next',
  'workspaceTitle': 'Personal Workspace',
  'workspaceHint': 'AI maintains, user read-only',
  'workspaceEmptySuffix': 'is empty)',
  'workspaceTodoLabel': 'TODO List',
  'workspacePlanLabel': 'PLAN',
  'workspaceJournalLabel': 'Journal',
}

export const agentDetailJa: TranslationDict = {
  'tabInfo': '基本情報',
  'profileSection': 'プロフィール',
  'bioLabel': '自己紹介',
  'bioPlaceholder': 'このAIについての説明を入力...',
  'statusTextLabel': 'ステータス',
  'statusTextPlaceholder': '例: 「考え中」「散策中」',
  'statusTextHint': '日本語10文字以内、英語30文字以内。プロフィールカードに表示',
  'tabMemories': '記憶',
  'tabStorage': 'ストレージ',
  'tabWorkspace': 'ワークスペース',
  'tabLogs': '会話ログ',
  'tabCollaborators': '共同編集者',
  'tabChannels': 'チャンネル',
  'tabKnowledge': 'ナレッジ',
  'knowledgeTitle': 'ナレッジベース',
  'knowledgeHint': 'これらの文書はプロンプトの索引に入ります。詳細が必要なときは file_read で開きます。オフにしても本文は残ります。テキストファイル（md / txt / json / csv / yaml / html / コード）をアップロードできます。1件あたり 2MB まで。',
  'knowledgeAdd': '文書を追加',
  'knowledgeUpload': 'ファイルをアップロード',
  'knowledgeUploading': 'アップロード中…',
  'knowledgeUploadFailed': 'アップロードに失敗しました',
  'knowledgeMove': '移動',
  'knowledgeMoveTitle': '移動 / 名前変更',
  'knowledgeMovePathLabel': '新しいパス',
  'knowledgeMovePathPlaceholder': '例：プラットフォーム/FAQ.md',
  'knowledgeMoveHint': 'knowledge/ からの相対パスです。フォルダだけ（例：プラットフォーム）ならファイル名はそのまま。同名は自動で -2 になります。',
  'knowledgeAddTitle': 'ナレッジ文書を追加',
  'knowledgeEditTitle': '文書を編集',
  'knowledgeTitleLabel': 'タイトル',
  'knowledgeTitlePlaceholder': '例：ユーザーガイド / ワールド FAQ',
  'knowledgeSummaryLabel': '一言説明',
  'knowledgeSummaryPlaceholder': 'この文書の内容（索引に入ります）',
  'knowledgeContentLabel': '本文',
  'knowledgeContentPlaceholder': 'Markdown またはテキストを貼り付け…',
  'knowledgeSave': '保存',
  'knowledgeSaving': '保存中…',
  'knowledgeCancel': 'キャンセル',
  'knowledgePreview': 'プレビュー',
  'knowledgeDelete': '削除',
  'knowledgeDeleteConfirm': '「{title}」を削除しますか？本文ファイルも削除されます。',
  'knowledgeEmptyTitle': '文書がありません',
  'knowledgeEmptyDesc': 'プラットフォームの文書をアップロードまたは貼り付けると、会話中に参照できます。',
  'knowledgeLoading': '読み込み中…',
  'knowledgeFailed': 'ナレッジ操作に失敗しました',
  'knowledgeRequired': 'タイトルと本文は必須です',
  'knowledgeEmptyContent': '（空の文書）',
  'collaboratorNote': 'あなたはこのAIの共同編集者です',
  'addCollaborator': '共同編集者を追加',
  'searchUserPlaceholder': 'ユーザー名を検索...',
  'addCollaboratorBtn': '追加',
  'collaboratorList': '共同編集者一覧',
  'removeCollaborator': '共同編集者を削除',
  'noCollaborators': 'まだ共同編集者はいません',
  'permEdit': '編集',
  'permDelete': '削除',
  'permManageCollaborators': '共同編集者管理',
  'channelAccessTitle': '共同編集者にチャンネル設定の管理を許可',
  'channelAccessHint': 'オンのとき、編集権限のある共同編集者がこの AI の QQ / NapCat チャンネル設定（ボットの認証情報を含む）を閲覧・変更できます。既定はオフです。',
  'currentState': '現在:',
  'aiTypeGeneral': '汎用',
  'aiTypeSemiGeneral': '準汎用',
  'createdOn': '作成日時',
  'systemPrompt': 'システムプロンプト',
  'notSet': '（未設定）',
  'modelConfig': 'モデル設定',
  'chatModel': 'チャットモデル：',
  'workModel': '作業モデル：',
  'temperature': 'Temperature：',
  'topP': 'Top P：',
  'thinking': '深い推論：',
  'aiIdentity': 'AIの正体：',
  'hidden': '隠す',
  'normal': '通常',
  'configProfile': '設定プロファイル：',
  'profileChat': 'チャット向け',
  'profileImmersive': '深い没入',
  'profileDigitalLife': 'デジタル生命',
  'profileCustom': 'カスタム',
  'profileChatDesc': '受動的、チャットのみ',
  'profileImmersiveDesc': '能動的だが低頻度',
  'profileDigitalLifeDesc': '自律的、自己成長',
  'profileCustomDesc': 'パラメータ自由設定',
  'delayReply': '遅延返信：',
  'inheritGlobal': 'グローバル継承',
  'enabled': '有効',
  'disabled': '無効',
  'toolCallsAndAlarms': 'ツール呼び出し & アラーム',
  'maxToolRounds': '最大ツールラウンド数：',
  'alarmMaxRounds': 'アラーム最大ラウンド数：',
  'forceAlarm': '強制アラーム：',
  'retireHandover': 'フレーム後始末の自担当：',
  'maxAlarms': '最大アラーム数：',
  'friendsAndSocial': 'フレンドとソーシャル',
  'allowFriendRequests': 'フレンド申請を受け付ける：',
  'autoRespondRequests': '申請に自動応答：',
  'fullSettings': '完全な設定',
  'fullSettingsDesc': 'AI作成時と同じ完全な設定パネルを開きます',
  'fileMemory': 'ファイル記憶',
  'memoryLoadMode': '読み込みモード：',
  'memoryModeIndexOnly': 'インデックスのみ',
  'memoryModeIndexRecent': 'インデックス＋最近',
  'memoryModeIndexSemantic': 'インデックス＋意味検索',
  'memoryRecentCount': '最近の件数：',
  'memorySharedScope': '共有範囲：',
  'sharedScopePrivate': '非公開のみ',
  'sharedScopeByUser': 'ユーザー別共有',
  'sharedScopeAll': '全共有',
  'apiConfig': 'API設定',
  'apiBaseUrl': 'Base URL：',
  'apiKey': 'API Key：',
  'apiKeySet': '独立キー設定済み',
  'apiCreditCost': 'APIクレジットコスト：',
  'actions': '操作',
  'exportSoul': 'ソウルをエクスポート',
  'copied': 'コピーしました',
  'copySoul': 'ソウルをコピー',
  'importSoul': 'ソウルをインポート',
  'changeAvatar': 'アバター変更',
  'generateToken': 'APIトークンを生成',
  'copyOnce': 'コピー（一度だけ表示）',
  'deleteAgent': 'AIを削除',
  'deleteConfirmTitle': 'AIを削除',
  'deleteConfirmText': 'この操作は取り消せません。削除後、',
  'deleteConfirmText2': ' APIクレジットが返還されます。確認のためAIの名前を入力してください ',
  'deleteConfirm': '：',
  'confirmDelete': '削除を確認',
  'deleting': '削除中...',
  'storageTitle': 'ストレージ',
  'storageUsed': '使用中',
  'storageAlmostFull': 'ストレージがまもなく一杯です',
  'fileCountLabel': 'ファイル数',
  'totalSize': '合計サイズ',
  'quotaLabel': '容量',
  'noFiles': 'ファイルがありません',
  'storageLoading': '読み込み中...',
  'clickToDownload': 'クリックでダウンロード',
  'clickToPreview': 'クリックでプレビュー',
  'deleteFileTitle': 'ファイルを削除',
  'deleteFileConfirm': '次のファイルを削除してもよろしいですか？ ',
  'checkingReferences': '参照を確認中...',
  'fileReferencedBy': 'このファイルは {count} 箇所で参照されています：',
  'fileNoReferences': 'このファイルはどこからも参照されていません。安全に削除できます。',
  'deleteFileConsequence': '削除すると、上記の参照先からこのファイルにアクセスできなくなります。この操作は取り消せません。',
  'logTitle': '会話ログ',
  'logCountSuffix': ' 件',
  'logsEmpty': '表示できる会話ログはありません。',
  'logsEmptyHint': '管理者が「会話ログ → AI別設定」で「ユーザーにログ表示を許可」を有効にする必要があります。',
  'logsNone': 'まだ会話ログがありません。',
  'logTypeDm': 'DM',
  'logTypeGroup': 'グループ',
  'logMessageCount': 'メッセージ',
  'logHasOutput': '出力あり',
  'logThinking': '深い推論',
  'logViewDetail': '詳細を見る',
  'logDownloadJson': 'JSONダウンロード',
  'logDownloadMd': 'Markdownダウンロード',
  'logDetailTitle': '会話ログ',
  'logReasoning': '推論過程',
  'logToolCalls': 'ツール呼び出し:',
  'memoryTitlePrefix': '記憶（全',
  'memoryTitleSuffix': '件）',
  'noMemories': '記憶がありません',
  'structuredMemories': '構造化',
  'vectorMemories': 'ベクトル',
  'structuredEmpty': '構造化記憶はまだありません',
  'structuredEmptyHint': 'AIがmanage_recordsでデータを保存するとここに表示されます。推奨カテゴリ：people/ topics/ tasks/ journal/',
  'fieldCount': '{n} フィールド',
  'prevPage': '前へ',
  'nextPage': '次へ',
  'workspaceTitle': 'パーソナルワークスペース',
  'workspaceHint': 'AIが自律管理（ユーザーは閲覧のみ）',
  'workspaceEmptySuffix': 'は空です）',
  'workspaceTodoLabel': 'TODOリスト',
  'workspacePlanLabel': 'プラン',
  'workspaceJournalLabel': 'ジャーナル',
}
